import { i18n, Messages } from '@lingui/core'
import { detect, fromUrl, fromStorage, fromNavigator, fromCookie } from '@lingui/detect-locale'
import * as yup from 'yup'
import './yupExtensions'
import { languagePolicy, resolveLanguage } from './language-policy'
import legacyI18n from 'i18next'

const messages = {
  ar: {},
  'ar-SA': {},
  en: {},
  'en-SA': {},
  'en-US': {},
  es: {},
  fr: {},
  it: {},
  iv: {},
  pt: {},
}

const langSchema = yup
  .string<Lang>()
  .oneOf(Object.keys(messages) as Lang[])
  .defined()
  .catchDefault('en')

/**
 * Resolve browser and profile language values to a catalog supported by the app.
 * Prefer an exact match (notably en-US), then the base language, and finally
 * English so an unsupported locale can never silently switch the UI to French.
 */
export function normalizeLanguage(language?: string | null): Lang {
  const candidate = language?.trim()
  if (!candidate) return 'en'

  const exactMatch = Object.keys(messages).find((locale) => locale.toLowerCase() === candidate.toLowerCase())
  if (exactMatch) return langSchema.validateSync(exactMatch)

  return langSchema.validateSync(candidate.split('-')[0].toLowerCase())
}

// use the lookup from i18next browser language detector for now for backward compatibility
export const defaultLanguage = languagePolicy.defaultLanguage ?? normalizeLanguage(
  detect(fromUrl('lng'), fromNavigator(), fromCookie('i18next'), fromStorage('i18nextLng'), () => 'en')
)

export type Lang = keyof typeof messages

let latestActivationRequest = 0
let latestAppActivationRequest = 0

export async function loadLinguiMessages(lang: Lang) {
  if (messages[lang] && Object.keys(messages[lang]).length > 0) {
    return messages[lang]
  }

  messages[lang] = await importMessages(lang)

  return messages[lang]
}

export async function activateLingui(lang: Lang) {
  const activationRequest = ++latestActivationRequest
  const messages = await loadLinguiMessages(lang)

  // Locale imports are asynchronous. Ignore a slower, stale request so an
  // initial browser locale cannot overwrite the authenticated user's locale.
  if (activationRequest !== latestActivationRequest) return

  i18n.load(lang, messages)
  i18n.activate(lang)
}

/** Activate the same deployment-approved locale in both translation engines. */
export async function activateAppLanguage(value?: string | null): Promise<void> {
  const activationRequest = ++latestAppActivationRequest
  const language: Lang = resolveLanguage(value ?? defaultLanguage)
  await Promise.all([activateLingui(language), legacyI18n.changeLanguage(language)])

  // A slower, overlapping activation must not win. activateLingui already
  // ignores stale requests; re-assert i18next only for the latest request so
  // the two engines can never settle on different locales.
  if (activationRequest !== latestAppActivationRequest) return
  if (legacyI18n.language !== language) await legacyI18n.changeLanguage(language)
  document.documentElement.lang = language
}

async function importMessages(lang: Lang) {
  const { messages } = await import(`@/locales/${lang}/messages.po`)
  return messages as Messages
}
